import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent } from "@/components/ui/card"
import { TrendingUp, ArrowRight } from "lucide-react"
import { useAuth } from "@/context/auth/auth-context"
import { useCurrency } from "@/context/currency/currency-context"
import hive from "@/lib/api/hive"
import type { FireSettings } from "@/types/fire"
import { FireWidgetSkeleton } from "@/components/custom/skeletons"

interface FireSummaryWidgetProps {
	annualIncome: number // already in display currency (from existing aggregated stats)
}

function getCoverageColorClass(pct: number): string {
	if (pct >= 75) return "text-green-500 dark:text-green-400"
	if (pct >= 50) return "text-amber-500 dark:text-amber-400"
	if (pct >= 25) return "text-orange-500 dark:text-orange-400"
	return "text-red-500 dark:text-red-400"
}

function getIconColorClass(pct: number): string {
	if (pct >= 75) return "text-green-500"
	if (pct >= 50) return "text-amber-500"
	if (pct >= 25) return "text-orange-500"
	return "text-red-500"
}

export function FireSummaryWidget({ annualIncome }: FireSummaryWidgetProps) {
	const { token } = useAuth()
	const { format, convert } = useCurrency()
	const [settings, setSettings] = useState<FireSettings | null>(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!token) return
		hive.getFireSettings(token)
			.then(setSettings)
			.catch(() => {})
			.finally(() => setLoading(false))
	}, [token])

	const annualGoal = settings ? settings.monthly_expense_target * 12 : 0
	// annualIncome is already in display currency (passed pre-converted from DashboardPage).
	// annualGoal is in settings.currency — convert to display currency for comparison.
	const coveragePct =
		annualGoal > 0 ? Math.round((annualIncome / convert(annualGoal, settings?.currency ?? "USD")) * 100) : 0
	const clampedPct = Math.min(coveragePct, 100)

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30">
			<CardContent className="pt-4 pb-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<TrendingUp
							className={`h-4 w-4 ${settings && annualGoal > 0 ? getIconColorClass(coveragePct) : "text-sky-500"}`}
						/>
						<span className="text-sm font-medium">FIRE Progress</span>
					</div>
					<Link
						to="/fire"
						className="text-sm text-sky-600 dark:text-sky-400 flex items-center gap-1 hover:underline"
					>
						View Dashboard <ArrowRight className="h-3 w-3" />
					</Link>
				</div>
				{loading ? (
					<FireWidgetSkeleton />
				) : settings && annualGoal > 0 ? (
					<div className="mt-3 flex items-end gap-4">
						<div>
							<p className={`text-2xl font-bold ${getCoverageColorClass(coveragePct)}`}>{coveragePct}%</p>
							<p className="text-xs text-muted-foreground">of goal covered</p>
						</div>
						<div className="flex-1">
							{/* Gradient track: full red→orange→amber→green revealed by progress */}
							<div
								className="h-3 rounded-full relative overflow-hidden"
								style={{
									background:
										"linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 55%, #22c55e 100%)",
								}}
							>
								{/* Gray overlay covers the unfilled right portion */}
								<div
									className="absolute inset-y-0 right-0 bg-gray-100 dark:bg-gray-800 transition-all duration-500"
									style={{ width: `${100 - clampedPct}%` }}
								/>
							</div>
							<p className="text-xs text-muted-foreground mt-1">
								{format(annualIncome, { skipConversion: true })}/yr of{" "}
								{format(convert(annualGoal, settings?.currency ?? "USD"), { skipConversion: true })}/yr
								goal
							</p>
						</div>
					</div>
				) : (
					<p className="text-sm text-muted-foreground mt-2">
						<Link to="/fire" className="text-sky-600 dark:text-sky-400 hover:underline">
							Set up your FIRE goal →
						</Link>
					</p>
				)}
			</CardContent>
		</Card>
	)
}
